/**
 * O que faz: Fornece estrutura visual compartilhada para páginas públicas do blog e hubs.
 * Onde usa: BlogIndexPage, BlogPostPage, BlogCategoryPage e ContentHubPage.
 * Conexões: Usa React Router para links internos, lucide-react e CSS isolado de conteúdo público.
 * Tabelas: Não interage com Supabase; apenas organiza navegação e contexto visual.
 * Data: 12/05/2026
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, Instagram, Linkedin, MessageCircle, Youtube } from "lucide-react";
import "@/styles/public-content.css";
import { SimboloGens } from "@/components/brand/MarcaGens";

type PublicContentLayoutProps = {
  title: string;
  description: string;
  children: ReactNode;
  showHero?: boolean;
};

const PublicContentLayout = ({ title, description, children, showHero = true }: PublicContentLayoutProps) => {
  return (
    <div className="public-content">
      <header className="public-header">
        <div className="public-header-inner">
          <Link to="/landing-v2" className="public-brand">
            <span className="public-brand-mark" aria-hidden="true"><SimboloGens /></span>
            <span>Gens CRM</span>
          </Link>
          <nav className="public-nav">
            <Link to="/blog">Blog</Link>
            <Link to="/blog" className="public-nav-categories">
              Categorias
              <ChevronDown size={14} />
            </Link>
            <Link to="/login">Entrar</Link>
            <Link to="/landing-v2" className="public-header-cta">
              Começar grátis
              <ArrowRight size={16} />
            </Link>
          </nav>
        </div>
      </header>

      <main className="public-main">
        {showHero ? (
          <section className="public-hero">
            <h1>{title}</h1>
            <p>{description}</p>
          </section>
        ) : null}
        {children}
      </main>

      <footer className="public-footer">
        <div className="public-footer-brand">
          <Link to="/landing-v2" className="public-footer-logo">
            <span className="public-brand-mark" aria-hidden="true"><SimboloGens /></span>
            <strong>Gens CRM</strong>
          </Link>
          <p>CRM para clínicas que atendem pelo WhatsApp. Funil, agenda, IA e follow-up em uma plataforma simples e poderosa.</p>
          <a className="public-footer-whatsapp" href="https://wa.me/5583996886297" target="_blank" rel="noreferrer">
            <MessageCircle size={15} />
            Falar no WhatsApp
          </a>
        </div>
        <div className="public-footer-column">
          <strong>Produto</strong>
          <Link to="/landing-v2">Recursos</Link>
          <Link to="/landing-v2">Planos</Link>
          <Link to="/landing-v2">Integrações</Link>
          <Link to="/politica-de-privacidade">Segurança</Link>
        </div>
        <div className="public-footer-column">
          <strong>Conteúdo</strong>
          <Link to="/blog">Blog</Link>
          <Link to="/crm-para-dentistas">Guias e materiais</Link>
          <Link to="/crm-para-clinicas-medicas">Casos de uso</Link>
          <Link to="/crm-para-clinicas-esteticas">Webinars</Link>
        </div>
        <div className="public-footer-column">
          <strong>Empresa</strong>
          <Link to="/landing-v2">Sobre nós</Link>
          <Link to="/landing-v2">Carreiras</Link>
          <Link to="/landing-v2">Contato</Link>
          <Link to="/landing-v2">Parceiros</Link>
        </div>
        <div className="public-footer-column public-footer-contact">
          <strong>Contato</strong>
          <p>Suporte: (83) 99688-6297</p>
          <p>comercial@genscrm.com.br</p>
          <p>Seg a Sex, 08h às 18h</p>
          <div className="public-footer-social">
            <a href="https://www.instagram.com/" aria-label="Instagram" target="_blank" rel="noreferrer">
              <Instagram size={16} />
            </a>
            <a href="https://www.linkedin.com/" aria-label="LinkedIn" target="_blank" rel="noreferrer">
              <Linkedin size={16} />
            </a>
            <a href="https://www.youtube.com/" aria-label="YouTube" target="_blank" rel="noreferrer">
              <Youtube size={16} />
            </a>
          </div>
        </div>
        <div className="public-footer-bottom">
          <span>© 2026 Gens CRM - Agência Gens • CNPJ 40.220.180/0001-31</span>
          <span>
            <Link to="/politica-de-privacidade">Privacidade</Link>
            <Link to="/politica-de-privacidade">Termos de uso</Link>
          </span>
        </div>
      </footer>
    </div>
  );
};

export default PublicContentLayout;
